import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { AdminRole } from '../../types/config';
import type { AdminPage, AdminPageProps } from '../../types/navigation';
import { useStore } from '../../contexts/StoreContext';
import { ADMIN_USERS } from '../../data/users';
import { AdminSidebar } from '../../components/admin/AdminSidebar';
import { AdminTopbar } from '../../components/admin/AdminTopbar';
import { pagesFor } from '../../utils/permissions';
import { availableForNote, needsVerification } from '../../utils/caseStatus';
import { AdminLogin } from './AdminLogin';
import { Dashboard } from './Dashboard';
import { CaseList } from './CaseList';
import { CaseDetail } from './CaseDetail';
import { Inventory } from './Inventory';
import { Stations } from './Stations';
import { WorkQueue } from './WorkQueue';
import { Verification } from './Verification';
import { Payments } from './Payments';
import { NoteSheets } from './NoteSheets';
import { Letters } from './Letters';
const queue = (page: AdminPage) => (p: AdminPageProps) => <WorkQueue {...p} page={page} data-id="element-805" data-mp="6j4r7dzri9hu" />;
const PAGES: Record<AdminPage, React.ComponentType<AdminPageProps>> = {
  dashboard: Dashboard,
  cases: CaseList,
  inventory: Inventory,
  stations: Stations,
  verification: Verification,
  payments: Payments,
  notesheet: NoteSheets,
  letters: Letters,
  handover: queue('handover'),
  reports: queue('reports'),
  users: queue('users'),
  config: queue('config'),
  audit: queue('audit')
};
export function AdminApp({
  onExit
}: {
  onExit: () => void;
}) {
  const {
    cases,
    noteFiles
  } = useStore();
  const [role, setRole] = useState<AdminRole | null>(null);
  const [page, setPage] = useState<AdminPage>('dashboard');
  const [focus, setFocus] = useState<string | undefined>();
  const [detail, setDetail] = useState<string | null>(null);
  const counts = useMemo(() => ({
    verification: cases.filter(needsVerification).length,
    payments: cases.filter(c => c.paymentStatus === 'paid' || c.paymentStatus === 'pending_verify').length,
    notesheet: cases.filter(availableForNote).length + noteFiles.filter(f => f.status === 'pending').length,
    letters: cases.filter(c => c.approval === 'approved' && (!c.letter || c.letter.status === 'draft' || c.letter.status === 'signed')).length
  }), [cases, noteFiles]);
  if (!role) return <AdminLogin onLogin={r => {
    setRole(r);
    setPage(defaultPage(r));
  }} onExit={onExit} data-id="element-806" data-mp="wsc89m85lza0" />;
  const allowed = pagesFor(role);
  const go = (p: AdminPage, pcr?: string) => {
    if (!allowed.includes(p)) {
      toast.error('এই মডিউল আপনার ভূমিকায় অনুমোদিত নয়।');
      return;
    }
    setPage(p);
    setFocus(pcr);
    setDetail(null);
  };
  const changeRole = (r: AdminRole) => {
    setRole(r);
    if (!pagesFor(r).includes(page)) setPage(defaultPage(r));
    toast.info(`ভূমিকা পরিবর্তিত: ${ADMIN_USERS[r].title}`);
  };
  const Current = PAGES[allowed.includes(page) ? page : 'dashboard'];
  const props: AdminPageProps = {
    role,
    go,
    openCase: pcr => setDetail(pcr),
    focus
  };
  return <div className="flex h-full w-full bg-canvas" data-id="element-807" data-mp="2gnzz8pk7ch4q">
      <AdminSidebar active={detail ? 'cases' : page} allowed={allowed} onNavigate={p => go(p)} counts={counts} data-id="element-808" data-mp="2gpks8aqvbs0h" />
      <div className="flex min-w-0 flex-1 flex-col" data-id="element-809" data-mp="2pnwg450cobp8">
        <AdminTopbar role={role} onRole={changeRole} onLogout={() => setRole(null)} onExit={onExit} onOpenCase={p => setDetail(p)} allowed={allowed} onNavigate={p => go(p)} data-id="element-810" data-mp="2pmbksd67ooqb" />
        <main className="flex-1 overflow-y-auto scroll-thin px-4 py-5 lg:px-8 lg:py-6" key={`${detail ?? page}-${role}`} data-id="element-811" data-mp="2zl14mbho4j97">
          {detail ? <CaseDetail {...props} pcr={detail} onBack={() => setDetail(null)} data-id="element-812" data-mp="33luu39are647" /> : <Current {...props} data-id="element-813" data-mp="38ffic9mjvlq7" />}
        </main>
      </div>
    </div>;
}
function defaultPage(r: AdminRole): AdminPage {
  const map: Partial<Record<AdminRole, AdminPage>> = {
    verifier: 'verification',
    dealing: 'notesheet',
    approver: 'notesheet',
    signatory: 'letters',
    accounts: 'payments',
    station: 'inventory'
  };
  return map[r] ?? 'dashboard';
}